import { defineStore } from 'pinia'
import { ref } from 'vue'
import { UserAPI } from '@/services/api'
import { getDeviceInfo } from '@/utils/device'
import { deviceStorage, userStorage } from '@/utils/storage'
import { appStorage } from '@/utils/storage'
import { tokenStorage } from '@/utils/storage'
import type { UserInfo } from '@/types/user'
import type { UserProfile } from '@/types/api'
import { HomeAPI } from '@/services/home'
import { useWaterfallStore } from '@/stores/waterfall'
import { hasAppLoginParams, decryptCommonParam } from '@/utils/appLogin'
import bridge from '@/utils/bridge'
import { useChatStore } from '@/stores/chat'
import * as Sentry from '@sentry/vue'
import { getSSEManager } from '@/utils/sse'
import { useRandomArrayStore } from '@/stores/useRandomArrayStore'


export const useUserStore = defineStore('user', () => {
  // 用户状态
  const isLoggedIn = ref(false)
  const token = ref('')
  const user = ref()
  const msgCost = ref(0)
  const appId = ref<any>('')
  const udid = ref<any>('')
  /**
   * 图片消息费用
   */
  const msgImgCost = ref(0)
  const equipmentInformation = ref({} as any)
  const isEditInfo = ref(false) // 是否修改过信息
  const isEmbeddedH5 = ref(false)
  const chatExpenses = ref(0)
  const isVersionUpdate = ref(false)
  const isThinking = ref(false)
  const isFirstGenerateImage = ref(false) // 是否是第一次生成图片
  const isFirstGenerateVideo = ref(false) // 是否是第一次生成视频
  const isFirstConfirmName = ref(false) // 是否是第一次确认名字
  const isFree = ref<any>([]) // 是否是免费用户
  const physicalStrength = ref() //设置体力购买费用
  const strengthBalance = ref()
  const isSuccessfulPayment = ref(false)
  // 初始化 isCheckButton，从 localStorage 读取，确保类型正确
  const getInitialCheckButton = () => {
    const stored = localStorage.getItem('is-check-button')
    if (stored === 'true') return true
    if (stored === 'false') return false
    return false // 默认值
  }
  const isCheckButton = ref<boolean>(getInitialCheckButton()) //开启继续说

  // 用户信息
  const userInfo = ref<UserProfile>({} as UserProfile),
    // 用户设置

    // 初始化用户数据
    /**
     * 初始化用户状态（从存储中恢复）
     */
    initializeAuth = async () => {
      // 优先处理 APP 传入的登录参数（appId & common）
      try {
        if (hasAppLoginParams()) {
          const appParams = new URLSearchParams(window.location.search)
          const appId = appParams.get('appId') || ''
          // 解密 common 获取 token（兼容字符串/对象，且容错控制字符）
          const commonRaw: any = decryptCommonParam()
          let tokenFromCommon: string | undefined
          let udid: string | undefined
          let isInto: boolean | undefined
          let loginFlag: boolean | any
          let userInfo: any

          if (typeof commonRaw === 'string') {
            // 移除不可见控制字符并提取第一个 JSON 片段
            const cleaned = commonRaw.trim().replace(/[\u0000-\u001F\u007F]/g, '')
            const match = cleaned.match(/\{[\s\S]*?\}/)

            if (match) {
              try {
                const parsed = JSON.parse(match[0])
                tokenFromCommon = parsed?.token
                udid = parsed?.udid
                isInto = parsed?.isInto
                loginFlag = parsed?.loginFlag
                localStorage.setItem('ai-chat-parsed', JSON.stringify(parsed))
                userStorage.setChatExpenses(userInfo.msgCost)
              } catch { }
            }
          } else if (commonRaw && typeof commonRaw === 'object') {
            tokenFromCommon = commonRaw.token
            udid = commonRaw.udid
            isInto = commonRaw.isInto
            loginFlag = commonRaw.loginFlag
            localStorage.setItem('ai-chat-parsed', JSON.stringify(commonRaw))
          }

          if (tokenFromCommon) {
            // 保存 token 与 appId 、udid
            setToken(tokenFromCommon)
            setudid(udid)
            setAppId(appId)
            setIsEmbeddedH5(isInto || false)
            // 拉取用户信息
            await getUserInfo()
            setDeviceInfo()
            // 标记登录与会话初始化
            setIsLoggedIn(loginFlag)
            sessionStorage.setItem('session_initialized', 'true')
            // 清理 URL 敏感参数
            // clearAppLoginParams()
            // 重新连接 SSE
            reconnectSSE()

            return
          }
        } else {
          const token = getStoredToken()

          // 检查是否已经进行过游客登录（localStorage）
          const hasTouristLoggedIn = userStorage.getTouristLoginFlag()

          // 检查当前会话是否已经初始化过（sessionStorage）
          const sessionInitialized = sessionStorage.getItem('session_initialized')

          // 只有在以下条件都满足时才进行游客登录：
          // 1. 没有有效token
          // 2. 从未进行过游客登录（localStorage检查）
          // 3. 当前会话未初始化过（sessionStorage检查，防止同一会话多次初始化）
          if (!token && !hasTouristLoggedIn) {
            try {
              await loginTouristFunc()

              // 设置游客登录标记，表示已经进行过游客登录
              userStorage.setTouristLoginFlag(true)

              // 设置会话初始化标记，防止同一会话重复初始化
              sessionStorage.setItem('session_initialized', 'true')
            } catch (error) {
              console.error('自动登录失败:', error)
            }
          } else if (!token && hasTouristLoggedIn) {
            // 如果已经进行过游客登录但没有token，说明用户可能主动登出了
            console.log('用户已进行过游客登录，当前无有效token')
          } else if (sessionInitialized) {
            console.log('当前会话已初始化，跳过自动登录')
          }

          // 只有在有有效 token 时才获取用户信息
          if (token) {
            try {
              await getUserInfo()
            } catch (error) {
              console.error('获取用户信息失败343242:', error)
              // 如果获取用户信息失败，清除无效的 token
              removeToken()
              // 设置会话初始化标记，防止同一会话重复初始化
              sessionStorage.setItem('session_initialized', 'true')
            }
          } else if (!token && hasTouristLoggedIn) {
            // 如果已经进行过游客登录但没有token，说明用户可能主动登出了
            console.log('用户已进行过游客登录，当前无有效token')
          } else if (sessionInitialized) {
            console.log('当前会话已初始化，跳过自动登录')
          }

          // // 只有在有有效 token 时才获取用户信息
          // if (token) {
          //   try {
          //     await getUserInfo()
          //   } catch (error) {
          //     console.error('获取用户信息失败:', error)
          //     // 如果获取用户信息失败，清除无效的 token
          //     tokenStorage.removeToken()
          //   }
          // }
        }
      } catch (e) {
        console.error('APP 登录参数处理失败:', e)
        // 失败则退回原有自动登录流程
      }
    },
    /**
     * 设置设备信息
     */
    setDeviceInfo = async () => {
      const response = await UserAPI.loginTourist()
      localStorage.setItem(
        'ai-chat-user',
        JSON.stringify({ ...response.data, msgCost: response?.data?.msgCost })
      )
      setEquipmentInformation(response.data)
      // 设置体力费用
      setPhysicalStrength(response.data?.config?.strengthPrice)
      userStorage.setChatExpenses(response?.data?.config?.msgCost)
    },
    /**
     * 设置体力费用
    */
    setPhysicalStrength = (data: any) => {
      physicalStrength.value = data
    },
    /**
     * 获取体力费用
    */
    getPhysicalStrength = () => {
      return physicalStrength.value
    },
    /**
     * 设置体力
    */
    setStrengthBalance = (data: any) => {
      strengthBalance.value = data
    },
    /**
     * 设置体力
    */
    getStrengthBalance = () => {
      return strengthBalance.value
    },
    /**
     * 获取设备信息
     */
    setEquipmentInformation = (data: any) => {
      equipmentInformation.value = data
      userStorage.setDeviceInfo(data)
    },
    /**
     * 获取设备信息
     */
    getEquipmentInformation = () => {
      equipmentInformation.value = userStorage.getDeviceInfo()
      return equipmentInformation.value
    },
    /**
     * 只用于设置信息的
     */
    setLoginTouristFunc = async () => {

      const response = await UserAPI.loginTourist()

      setEquipmentInformation(response.data)
      // 设置体力
      setPhysicalStrength(response.data?.config?.strengthPrice)


      user.value = response.data as unknown as UserInfo

      /**
       * 设置是否是免费用户
       */
      if (user.value?.roleFreeList) {
        setIsFree(user.value?.roleFreeList)
      }
      if (user?.value.config?.msgCost) {
        userStorage.setChatExpenses(user.value.config.msgCost)
      }
      if (user?.value.config?.msgImgCost) {
        userStorage.setMsgImgCost(user.value.config.msgImgCost)
      }
    },
    /**
     * 游客登陆
     */
    loginTouristFunc = async () => {
      try {
        // 如果不在嵌入式H5中，则设置appId
        if (!isEmbeddedH5.value) {
          setAppId(localStorage.getItem('ai-chat-app-id') || '')
        }

        const response = await UserAPI.loginTourist()

        user.value = response.data as unknown as UserInfo

        localStorage.setItem('ai-chat-user', JSON.stringify(user.value))
        /**
         * 设置token
         */
        if (user.value?.accessToken) {
          setToken(user.value.accessToken)
        }
        /**
         * 设置是否是免费用户
         */
        if (user.value?.roleFreeList) {
          setIsFree(user.value?.roleFreeList)
        }
        /**
         * 设置消息费用
         */
        if (user?.value.config?.msgCost) {
          userStorage.setChatExpenses(user.value.config.msgCost)
        }
        /**
         * 设置图片消息费用
         */
        if (user?.value.config?.msgImgCost) {
          userStorage.setMsgImgCost(user.value.config.msgImgCost)
        }
        // 设置设备信息
        setEquipmentInformation(response.data)
        // 设置体力
        setPhysicalStrength(response.data?.config?.strengthPrice)


        // 设置设备信息到storage
        const deviceInfo = getDeviceInfo()
        deviceStorage.setUdid(deviceInfo.udid)
        setIsLoggedIn(true)
        // 游客登录成功后重新连接SSE使用新token
        reconnectSSE()

        return user.value
      } catch (error) {
        throw error
      }
    },
    /**
     * 设置是否是免费用户
     */
    setIsFree = (flag: any) => {
      localStorage.setItem('isfreeRole', flag)
      isFree.value = flag
    },
    /**
     * 获取是否是免费用户
     */
    getIsFree = () => {
      return isFree.value || localStorage.getItem('isfreeRole')
    },
    // 设置udid
    setudid = (val: any) => {
      udid.value = val
      localStorage.setItem('ai-chat-udid', val)
    },
    // 获取udid
    getudid = () => {
      udid.value = localStorage.getItem('ai-chat-udid')
      return udid.value
    },
    // 设置appid
    setAppId = (val: any) => {
      appId.value = val
      appStorage.setAppId(val)
    },
    //获取appid
    getAppId = () => {
      return appId.value || appStorage.getAppId || ''
    },
    /**
     * 设置用户是否已支付成功
     */
    setIsSuccessfulPayment = (val: boolean) => {
      isSuccessfulPayment.value = val
    },
    /**
     * 获取用户是否已支付成功
     */
    getIsSuccessfulPayment = () => {
      return isSuccessfulPayment.value
    },
    /**
     * 设置用户是否已登录标记
     */
    setIsLoggedIn = (val: boolean) => {
      isLoggedIn.value = val
      userStorage.setIsLoggedIn(val)
    },
    /**
     * 获取用户是否已登录标记
     */
    getIsLoggedIn = () => {
      isLoggedIn.value = userStorage.getIsLoggedIn() || false
      return isLoggedIn.value
    },
    /**
     * 设置用户是否已登录标记
     */
    removeIsLoggedIn = () => {
      isLoggedIn.value = false
      userStorage.removeIsLoggedIn()
    },
    /**
     * 获取存储的token
     */
    getStoredToken = () => {
      const stored = tokenStorage.getToken()
      token.value = stored || ''
      return stored
    },
    /**
     * 获取游客登录标记
     */
    getTouristLoginFlag = () => {
      return userStorage.getTouristLoginFlag()
    },
    /**
     * 设置游客登录标记
     */
    setTouristLoginFlag = (flag: boolean) => {
      userStorage.setTouristLoginFlag(flag)
    },
    /**
     * 获取用户信息
     */
    getUserInfo = async () => {
      const response = await UserAPI.getUserProfile()
      userInfo.value = response.data as unknown as UserProfile
      userStorage.setTouristInfo(userInfo.value)
      setMsgCost(userInfo.value.balance)

      setStrengthBalance(userInfo.value.strengthBalance)
      // 设置 Sentry 用户信息，方便追踪错误
      if (userInfo.value?.userId) {
        Sentry.setUser({
          id: String(userInfo.value.userId),
          username: userInfo.value.nickname,
          email: userInfo.value.email
        })
      }
    },
    /**
     * 删除用户信息
     */
    removeUserInfo = () => {
      userInfo.value = {} as UserProfile
      userStorage.removeTouristInfo()
      removeMsgCost()
    },
    /**
     * 获取游客信息
     */
    getTouristInfo = () => {
      return userStorage.getTouristInfo()
    },
    /**
     * 设置token
     */
    setToken = (newToken: string) => {
      token.value = newToken
      tokenStorage.setToken(newToken)
    },
    /**
     * 删除token
     */
    removeToken = () => {
      token.value = ''
      tokenStorage.removeToken()
    },
    /**
     * 设置消息费用
     */
    setMsgCost = (num: number) => {
      msgCost.value = num
      userStorage.setMsgCost(num)
    },
    /**
     * 获取消息费用
     */
    getMsgCost = () => {
      msgCost.value = userStorage.getMsgCost()
      return msgCost.value
    },
    /**
     * 删除消息费用
     */
    removeMsgCost = () => {
      msgCost.value = 0
      userStorage.removeMsgCost()
    },
    /**
     * 设置是否编辑信息
     */
    setIsEditInfo = (flag: boolean) => {
      isEditInfo.value = flag
      userStorage.setIsEditInfo(flag)
    },
    /**
     * 获取是否编辑信息
     */
    getIsEditInfo = () => {
      isEditInfo.value = userStorage.getIsEditInfo()
      return isEditInfo.value
    },
    /**
     * 移除是否编辑信息
     */
    removeIsEditInfo = () => {
      isEditInfo.value = false
      userStorage.removeIsEditInfo()
    },
    /**
     * 删除用户
     */
    deleteUser = async () => {
      await HomeAPI.deleteUser({})
      removeIsEditInfo()
      await logOut()
    },
    /**
     * 退出登录
     */

    logOut = () => {
      const chatStore = useChatStore()
      const waterfallStore = useWaterfallStore()
      waterfallStore.removeItem()
      removeToken()
      userStorage.removeIsLoggedIn()
      userStorage.removeTouristInfo()
      removeUserInfo()
      removeMsgCost()
      removeIsLoggedIn()
      localStorage.removeItem('ai-chat-store')
      userStorage.removeDetailInfo()
      localStorage.removeItem('rating_message_count')
      localStorage.removeItem('rating_last_cancel_date')
      localStorage.removeItem('rating_has_rated')
      userStorage.removeTouristLoginFlag()
      localStorage.removeItem('ai-chat-user')
      localStorage.removeItem('ai-chat-ephemeral')
      localStorage.removeItem('checkin_last_date')
      localStorage.removeItem('checkin_last_reset_week')
      userStorage.removeIsThinking()
      localStorage.removeItem('modelIndex')
      localStorage.removeItem('isSginIn')
      localStorage.removeItem('today')
      chatStore.clearLocalCache()
      indexedDB.deleteDatabase('RoleplayH5')
      localStorage.removeItem('is_store_popup')
      localStorage.removeItem('nowMsgCost')
      localStorage.removeItem('selectedChannelIndex')
      localStorage.removeItem('first_pay_success_to_vippage')
      localStorage.removeItem('first_pay_success_to_tabstore')
      localStorage.removeItem('is-check-button')
    },
    /**
     * 重新连接SSE（用于登录成功后）
     */
    reconnectSSE = () => {
      const sseManager = (window as any).$sseManager
      if (sseManager && typeof sseManager.reconnectWithNewToken === 'function') {
        // 使用管理器内置的安全重连，避免与自动重连产生竞态
        sseManager.reconnectWithNewToken()
      } else if (sseManager) {
        // 兜底：保持原有行为
        sseManager.disconnect()
        setTimeout(() => {
          sseManager.connect()
        }, 100)
      } else {
        // SSE 管理器不存在，创建新的
        const newSSEManager = getSSEManager()
        newSSEManager.connect()
      }
    },
    /**
     * 登录
     */
    login = async (data: any) => {
      const response = await UserAPI.login(data)
      user.value = response.data as unknown as UserInfo
      setToken(response.data.accessToken)
      bridge.call('appRefreshToken', {
        token: response.data.accessToken
      })
      await getUserInfo()
      setIsLoggedIn(false)

      // 登录成功后重新连接SSE使用新token
      reconnectSSE()

      // 初始化 SSE 连接
      const chatStore = useChatStore()
      chatStore.initializeSSE()
    },
    /**
     * 注册
     */
    register = async (data: any) => {
      const response = await UserAPI.register(data)
      user.value = response.data as unknown as UserInfo
      setToken(response.data.accessToken)
      const randomStore = useRandomArrayStore()

      bridge.call(randomStore.randomArray[6], {
        token: response.data.accessToken
      })
      await getUserInfo()
      setIsLoggedIn(false)
      // 注册成功后重新连接SSE使用新token
      reconnectSSE()

      // 初始化 SSE 连接
      const chatStore = useChatStore()
      chatStore.initializeSSE()
    },
    /**
     * 谷歌登录
     */
    googleLogin = async (data: any) => {
      const response = await UserAPI.googleLogin(data)
      user.value = response.data as unknown as UserInfo
      setToken(response.data.accessToken)
      setIsLoggedIn(false)
      await getUserInfo()

      // 谷歌登录成功后重新连接SSE使用新token
      reconnectSSE()

      // 初始化 SSE 连接
      const chatStore = useChatStore()
      chatStore.initializeSSE()
    },
    /**
     * 设置是否嵌入H5
     */
    setIsEmbeddedH5 = (flag: boolean) => {
      isEmbeddedH5.value = flag
      userStorage.setIsEmbeddedH5(flag)
    },
    /**
     * 获取是否嵌入H5
     */
    getIsEmbeddedH5 = () => {
      isEmbeddedH5.value = userStorage.getIsEmbeddedH5()
      return isEmbeddedH5.value
    },
    /**
     * 设置单挑消息费用
     */
    setChatExpenses = (num: number) => {
      chatExpenses.value = num
      userStorage.setChatExpenses(num)
    },
    /**
     * 获取单挑消息费用
     */
    getChatExpenses = () => {
      chatExpenses.value = userStorage.getChatExpenses()
      return chatExpenses.value
    },
    /**
     * 设置是否版本更新
     */
    setIsVersionUpdate = (flag: boolean) => {
      isVersionUpdate.value = flag
    },
    /**
     * 获取是否版本更新
     */
    getIsVersionUpdate = () => {
      return isVersionUpdate.value
    },
    /**
     * 开启继续说
     */
    setCheckButton = () => {
      isCheckButton.value = !isCheckButton.value

      // 确保正确保存为字符串类型
      localStorage.setItem('is-check-button', String(isCheckButton.value))
    },
    getCeckButton = () => {
      const stored = localStorage.getItem('is-check-button')
      isCheckButton.value = stored === 'true'
      return isCheckButton.value
    },
    /**
     * 设置是否思考
     */
    setIsThinking = (flag: boolean) => {
      isThinking.value = flag
      userStorage.setIsThinking(flag)
    },
    /**
     * 获取是否思考
     */
    getIsThinking = () => {
      isThinking.value = userStorage.getIsThinking()
      return isThinking.value
    },
    /**
     * 设置图片消息费用
     */
    setMsgImgCost = (num: number) => {
      msgImgCost.value = num
      userStorage.setMsgImgCost(num)
    },
    /**
     * 获取图片消息费用
     */
    getMsgImgCost = () => {
      msgImgCost.value = userStorage.getMsgImgCost()
      return msgImgCost.value
    },
    /**
     * 设置是否是第一次生成图片
     */
    setIsFirstGenerateImage = () => {
      localStorage.setItem('is_first_generate_image', 'true')
      isFirstGenerateImage.value = true
    },
    /**
     * 获取是否是第一次生成图片
     */
    getIsFirstGenerateImage = () => {
      isFirstGenerateImage.value = localStorage.getItem('is_first_generate_image') === 'true'
      return isFirstGenerateImage.value
    },
    /**
     * 设置是否第一次生视频
    */
    setIsFirstGenerateVideo = () => {
      localStorage.setItem('is_first_generate_video', 'true')
      isFirstGenerateVideo.value = true
    },
    /**
     * 获取是否第一次生视频
    */
    getIsFirstGenerateVideo = () => {
      isFirstGenerateVideo.value = localStorage.getItem('is_first_generate_video') === 'true'
      return isFirstGenerateVideo.value
    },
    /**
     * 设置是否是第一次确认名字
     */
    setIsFirstConfirmName = () => {
      isFirstConfirmName.value = true
      localStorage.setItem('is_first_confirm_name', 'true')
    },
    /**
     * 获取是否是第一次确认名字
     */
    getIsFirstConfirmName = () => {
      isFirstConfirmName.value = localStorage.getItem('is_first_confirm_name') === 'true'
      return isFirstConfirmName.value
    }
  return {
    // 状态
    isLoggedIn,
    isCheckButton,
    token,
    userInfo,
    msgCost,
    isEditInfo,
    isEmbeddedH5,
    isThinking,
    equipmentInformation,
    msgImgCost,
    isFirstGenerateImage,
    isFirstGenerateVideo,
    isFirstConfirmName,
    appId,
    udid,
    isFree,
    chatExpenses,
    physicalStrength,
    strengthBalance,
    setStrengthBalance,
    getStrengthBalance,
    setPhysicalStrength,
    getPhysicalStrength,
    setudid,
    getudid,
    setIsEmbeddedH5,
    getIsEmbeddedH5,
    removeToken,
    setIsEditInfo,
    getCeckButton,
    getIsEditInfo,
    removeIsEditInfo,
    setMsgCost,
    getMsgCost,
    removeMsgCost,
    setToken,
    getStoredToken,
    setTouristLoginFlag,
    getTouristLoginFlag,
    getIsLoggedIn,
    loginTouristFunc,
    getTouristInfo,
    getUserInfo,
    removeUserInfo,
    // 方法
    initializeAuth,
    deleteUser,
    logOut,
    reconnectSSE,
    login,
    register,
    googleLogin,
    setChatExpenses,
    getChatExpenses,
    setIsVersionUpdate,
    getIsVersionUpdate,
    setCheckButton,
    setDeviceInfo,
    setIsThinking,
    getIsThinking,
    setEquipmentInformation,
    getEquipmentInformation,
    setMsgImgCost,
    getMsgImgCost,
    setAppId,
    getAppId,
    setLoginTouristFunc,
    setIsFirstGenerateImage,
    getIsFirstGenerateImage,
    setIsFirstGenerateVideo,
    getIsFirstGenerateVideo,
    setIsFirstConfirmName,
    getIsFirstConfirmName,
    setIsFree,
    setIsSuccessfulPayment,
    getIsSuccessfulPayment,
    isSuccessfulPayment,
    getIsFree
  }
})
